import GLightbox from "glightbox";
import {isLoading as loaderIsLoading} from "./common/loader.js";
import {OverlayScrollbars} from "overlayscrollbars";

let currentLightbox;
let osInstance;

document.addEventListener("DOMContentLoaded", () => {
    window.isLoading = loaderIsLoading;
    prefetchForms();
    initPopupForms();
    initNinjaFormHandlers();
    initBusinessEmailValidation();
    window.addEventListener("message", handleIframeResize);

    document.addEventListener("click", event => {
        if (event.target.classList.contains("close-modal") && currentLightbox) {
            currentLightbox.close();
        }
    });
});

const initBusinessEmailValidation = () => {
    if (typeof ninjaFormsBusinessEmailValidation === 'undefined') {
        return;
    }

    const formValidationStates = new Map();
    const validationTimeouts = new Map();

    const isBusinessEmail = (email) => {
        if (!email || !ninjaFormsBusinessEmailValidation.excludedDomains) {
            return true;
        }

        const emailLower = email.toLowerCase().trim();

        for (let domain of ninjaFormsBusinessEmailValidation.excludedDomains) {
            const regex = new RegExp('@' + domain.replace('.', '\\.') + '$', 'i');
            if (regex.test(emailLower)) {
                return false;
            }
        }
        return true;
    };

    const showEmailError = (emailField, message) => {
        clearEmailError(emailField);

        const errorElement = document.createElement('div');
        errorElement.className = 'nf-error-msg business-email-error';
        errorElement.textContent = message;
        errorElement.style.cssText = 'font-size: 14px; margin-top: 5px; display: block;';

        emailField.classList.add('nf-error');
        emailField.setAttribute('aria-invalid', 'true');

        let insertLocation = emailField.parentElement;
        const possibleParents = [
            emailField.closest('.nf-field-element'),
            emailField.closest('.nf-field-container'),
            emailField.closest('.nf-field'),
            emailField.parentElement
        ];

        for (let parent of possibleParents) {
            if (parent) {
                insertLocation = parent;
                break;
            }
        }

        insertLocation.appendChild(errorElement);
    };

    const clearEmailError = (emailField) => {
        emailField.classList.remove('nf-error');
        emailField.setAttribute('aria-invalid', 'false');

        const possibleContainers = [
            emailField.closest('.nf-field-container'),
            emailField.closest('.nf-field-element'),
            emailField.closest('.nf-field'),
            emailField.parentElement,
            document
        ];

        possibleContainers.forEach(container => {
            if (container) {
                const existingErrors = container.querySelectorAll('.business-email-error');
                existingErrors.forEach(error => error.remove());
            }
        });
    };

    const updateSubmitButtonState = (formContainer, isValid) => {
        const submitButtons = formContainer.querySelectorAll('input[type="submit"], button[type="submit"], .nf-form-submit input, .nf-form-submit button');

        submitButtons.forEach(submitButton => {
            if (isValid) {
                submitButton.disabled = false;
                submitButton.style.cursor = '';
                submitButton.style.pointerEvents = '';
                submitButton.removeAttribute('data-business-email-disabled');
            } else {
                submitButton.disabled = true;
                submitButton.style.cursor = 'not-allowed';
                submitButton.style.pointerEvents = 'none';
                submitButton.setAttribute('data-business-email-disabled', 'true');
            }
        });
    };

    const validateEmailField = (emailField) => {
        const email = emailField.value.trim();
        const formContainer = emailField.closest('.nf-form-cont') || emailField.closest('form') || emailField.closest('.ninja-forms-form');

        if (!formContainer) {
            return true;
        }

        const formId = formContainer.id ? formContainer.id.replace('nf-form-', '').replace('-cont', '') : 'unknown';

        if (!email) {
            clearEmailError(emailField);
            formValidationStates.set(formId, true);
            updateSubmitButtonState(formContainer, true);
            return true;
        }

        const isValid = isBusinessEmail(email);
        formValidationStates.set(formId, isValid);

        if (!isValid) {
            showEmailError(emailField, ninjaFormsBusinessEmailValidation.errorMessage);
            updateSubmitButtonState(formContainer, false);
        } else {
            clearEmailError(emailField);
            updateSubmitButtonState(formContainer, true);
        }

        return isValid;
    };

    const debouncedValidation = (emailField) => {
        const fieldId = emailField.id || emailField.name;

        if (validationTimeouts.has(fieldId)) {
            clearTimeout(validationTimeouts.get(fieldId));
        }

        const timeoutId = setTimeout(() => {
            validateEmailField(emailField);
            validationTimeouts.delete(fieldId);
        }, 500);

        validationTimeouts.set(fieldId, timeoutId);
    };

    const setupEmailValidation = () => {
        const emailFields = document.querySelectorAll('input[type="email"]');

        emailFields.forEach((emailField) => {
            if (emailField.hasAttribute('data-business-validation-setup')) {
                return;
            }

            emailField.setAttribute('data-business-validation-setup', 'true');

            emailField.addEventListener('input', () => debouncedValidation(emailField));
            emailField.addEventListener('blur', () => {
                const fieldId = emailField.id || emailField.name;
                if (validationTimeouts.has(fieldId)) {
                    clearTimeout(validationTimeouts.get(fieldId));
                    validationTimeouts.delete(fieldId);
                }
                validateEmailField(emailField);
            });
            emailField.addEventListener('change', () => validateEmailField(emailField));

            if (emailField.value.trim()) {
                validateEmailField(emailField);
            }
        });

        const forms = document.querySelectorAll('.nf-form-cont');
        forms.forEach((formContainer) => {
            if (formContainer.hasAttribute('data-business-validation-setup')) {
                return;
            }

            formContainer.setAttribute('data-business-validation-setup', 'true');
            const formId = formContainer.id ? formContainer.id.replace('nf-form-', '').replace('-cont', '') : 'unknown';

            if (typeof nfRadio !== 'undefined') {
                nfRadio.channel('form-' + formId).on('submit:response', function(response) {
                    formValidationStates.delete(formId);
                });
            }
        });
    };

    if (typeof nfRadio !== 'undefined') {
        nfRadio.channel('forms').on('submit:validate', function(formModel) {
            const formId = formModel.get('id');
            const formContainer = document.getElementById('nf-form-' + formId + '-cont');

            if (!formContainer) return;

            const emailFields = formContainer.querySelectorAll('input[type="email"]');
            let hasInvalidEmail = false;

            emailFields.forEach(emailField => {
                const email = emailField.value.trim();
                if (email && !isBusinessEmail(email)) {
                    hasInvalidEmail = true;
                    showEmailError(emailField, ninjaFormsBusinessEmailValidation.errorMessage);

                    const fieldId = emailField.id.replace('nf-field-', '');
                    const fieldModel = formModel.get('fields').findWhere({id: parseInt(fieldId)});
                    if (fieldModel) {
                        fieldModel.trigger('ninja:invalid', ninjaFormsBusinessEmailValidation.errorMessage);
                    }
                }
            });

            if (hasInvalidEmail) {
                formValidationStates.set(formId, false);
                updateSubmitButtonState(formContainer, false);

                formModel.trigger('ninja:errors', [{
                    slug: 'business-email-validation',
                    message: ninjaFormsBusinessEmailValidation.errorMessage
                }]);
            } else {
                formValidationStates.set(formId, true);
                updateSubmitButtonState(formContainer, true);
            }
        });
    }

    setupEmailValidation();

    const observer = new MutationObserver((mutations) => {
        let shouldSetup = false;

        mutations.forEach((mutation) => {
            if (mutation.type === 'childList') {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        if (node.matches && (
                            node.matches('input[type="email"]') ||
                            node.matches('.nf-form-cont') ||
                            node.querySelector('input[type="email"]') ||
                            node.querySelector('.nf-form-cont')
                        )) {
                            shouldSetup = true;
                        }
                    }
                });
            }
        });

        if (shouldSetup) {
            setTimeout(setupEmailValidation, 100);
        }
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });
};

const initNinjaFormHandlers = () => {
    if (typeof Marionette === 'undefined' || typeof nfRadio === 'undefined') {
        return;
    }

    const HandlerController = Marionette.Object.extend({
        initialize: function() {
            this.listenTo(nfRadio.channel('form'), 'render:view', this.setupPardotHandler);
        },

        setupPardotHandler: function(view) {
            const formModel = view.model;
            const formEl = view.el;

            if (!formEl || !formModel) {
                return;
            }

            let domElement = formEl;
            if (formEl instanceof jQuery) {
                domElement = formEl[0];
            } else if (typeof formEl === 'string') {
                domElement = document.querySelector(formEl);
            }

            if (!domElement || typeof domElement.closest !== 'function') {
                return;
            }

            const wrapper = domElement.closest('.ninja-form-handler-wrapper');
            if (!wrapper) {
                return;
            }

            const handler = wrapper.getAttribute('data-handler');
            if (!handler) {
                return;
            }

            if (domElement.hasAttribute('data-pardot-handler-initialized')) {
                return;
            }

            domElement.setAttribute('data-pardot-handler-initialized', 'true');

            const setPardotValue = () => {
                const pardotField = domElement.querySelector('input[data-key="pardot_handler"]');

                if (pardotField) {
                    const fieldId = pardotField.id;

                    if (window.jQuery) {
                        window.jQuery('#' + fieldId).val(handler).trigger('change');
                    } else {
                        pardotField.value = handler;
                        pardotField.dispatchEvent(new Event('change', { bubbles: true }));
                    }

                    return true;
                }

                return false;
            };

            const initialSet = setPardotValue();

            if (!initialSet) {
                setTimeout(() => {
                    const retrySet = setPardotValue();
                    if (!retrySet) {
                        setTimeout(setPardotValue, 500);
                    }
                }, 100);
            }

            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                        setTimeout(setPardotValue, 50);
                    }
                });
            });

            observer.observe(domElement, {
                childList: true,
                subtree: true
            });

            domElement.addEventListener('submit', function(e) {
                setPardotValue();
            });
        }
    });

    new HandlerController();
};

const prefetchForms = () => {
    if (!Array.isArray(window.popupFormsData) || !window.popupFormsData.length) return;

    const prefetchedUrls = new Set();
    const formUrls = new Set(window.popupFormsData);
    document.querySelectorAll('a[href^="#"]').forEach(link => {
        const href = link.getAttribute("href");
        const formUrl = href.slice(1);
        if (formUrls.has(formUrl) && !prefetchedUrls.has(formUrl)) {
            const url = `${location.origin}/forms/?form_url=${encodeURIComponent(formUrl)}`;

            const linkEl = document.createElement("link");
            linkEl.rel = "prefetch";
            linkEl.as = "document";
            linkEl.href = url;
            document.head.appendChild(linkEl);
            prefetchedUrls.add(formUrl);
        }
    });
};

const initPopupForms = () => {
    if (!Array.isArray(window.popupFormsData) || !window.popupFormsData.length) return;
    const formUrls = new Set(window.popupFormsData);
    document.querySelectorAll('a[href^="#"]').forEach(link => {
        const href = link.getAttribute("href");
        const formUrl = href.slice(1);
        if (formUrls.has(formUrl)) {
            link.addEventListener("click", event => {
                event.preventDefault();
                openPopupForm(formUrl);
            });
        }
    });
};

const openPopupForm = (formUrl) => {
    const url = `${location.origin}/forms/?form_url=${encodeURIComponent(formUrl)}`;
    window.isLoading(true);
    document.body.classList.add("modal-form");

    currentLightbox = GLightbox({
        elements: [
            {
                content: `
                    <button class="close-modal" onclick="
                        window.parent.postMessage({ action: 'closeForm' }, window.location.origin);
                    "><span class="screen-reader-text">Close</span></button>
                    <div class="popup-content">
                        <iframe
                            src="${url}"
                            frameborder="0"
                            scrolling="no"
                            style="width:100%; height:100%;"
                            onload="window.isLoading(false)"
                        ></iframe>
                    </div>`,
            },
        ],
        width: "820px",
        height: "auto",
        closeButton: true,
        touchNavigation: false,
        loop: false,
        openEffect: "fade",
        closeEffect: "fade",
        draggable: false,
        zoomable: false,
    });

    currentLightbox.on("slide_after_load", () => {
        const inline = document.querySelector(".gslide-inline");
        if (!inline) return;

        if (osInstance) {
            osInstance.destroy();
            osInstance = null;
        }
        osInstance = OverlayScrollbars(inline, {
            scrollbars: {autoHide: "never"},
            overflow: {x: "hidden", y: "scroll"}
        });
    });

    currentLightbox.open();

    currentLightbox.on("close", () => {
        document.body.classList.remove("modal-form");
        window.isLoading(false);
        if (osInstance) {
            osInstance.destroy();
            osInstance = null;
        }
    });
};

const handleIframeResize = (event) => {
    if (event.origin !== location.origin || !currentLightbox) return;
    const {action, height} = event.data;

    if (action === "closeForm") {
        currentLightbox.close();
    }

    if (typeof height === "number") {
        const iframe = document.querySelector(".gslide iframe");
        if (iframe) {
            iframe.style.height = `${height}px`;
        }
    }
};
